import { ProductOption } from '@/services/matchingWeb.service';
import { COLOR_PALETTE } from '@/externals/ddm/constants/colorTypeConfig';

/**
 * 문자열에서 색상 코드를 추출하는 함수
 * @param text - 검사할 문자열
 * @returns 찾은 색상 코드 또는 null
 */
export const extractColorCode = (text: string): string | null => {
    // #로 시작하는 6자리 또는 3자리 16진수 색상 코드 매칭
    const hexMatch = text.match(/#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})\b/);
    if (hexMatch) return hexMatch[0];

    // rgb() 또는 rgba() 형식 매칭
    const rgbMatch = text.match(
        /rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)|rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)/,
    );
    if (rgbMatch) return rgbMatch[0];

    return null;
};

// 색상 코드를 색상 이름으로 변환하는 함수
const getColorNameFromCode = (colorCode: string, colorNameInfo?: Record<string, string>): string | null => {
    // colorNameInfo에 해당 색상 코드가 있으면 우선 사용
    if (colorNameInfo?.[colorCode]) {
        return colorNameInfo[colorCode];
    }
    // 없으면 기본 COLOR_PALETTE에서 찾기
    const color = COLOR_PALETTE.find((c) => c.value.toLowerCase() === colorCode.toLowerCase());
    return color ? color.name : null;
};

/**
 * 옵션 이름에서 색상 정보를 추출하는 함수
 * @param option - 상품 옵션 객체
 * @param colorNameInfo - 색상 이름 정보
 * @returns 색상 정보가 추가된 옵션 객체
 */
export function extractOptionColor(
    option: ProductOption,
    colorNameInfo?: Record<string, string> | undefined,
): ProductOption {
    // 이미 color 속성이 있다면 색상 이름으로 변환
    if (option.color) {
        const colorName = getColorNameFromCode(option.color, colorNameInfo);
        if (colorName) {
            // optionName에서 색상 코드를 색상 이름으로 대체
            const newOptionName = option.optionName.replace(option.color, colorName);
            return {
                ...option,
                optionName: newOptionName,
            };
        }
        return option;
    }

    const colorCode = extractColorCode(option.optionName);
    if (colorCode) {
        const colorName = getColorNameFromCode(colorCode, colorNameInfo);
        return {
            ...option,
            color: colorCode,
            // 색상 이름이 있으면 optionName 업데이트
            optionName: colorName ? option.optionName.replace(colorCode, colorName) : option.optionName,
        };
    }

    return option;
}
